"use client";

import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, MenuItem, Paper, Select,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography, InputLabel, FormControl,
} from "@mui/material";
import EditIcon    from "@mui/icons-material/Edit";
import DeleteIcon  from "@mui/icons-material/Delete";
import AddIcon     from "@mui/icons-material/Add";
import Swal        from "sweetalert2";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { getSweetAlert } from "../../helper";

const empty = { name: "", phone: "", telegram_chat_id: "", telegram_username: "", status: true, role: "cashier" };

export default function TelegramCashierPage() {
  const token = useSelector((s) => s.loginSlice.token);

  const [list, setList]     = useState([]);
  const [open, setOpen]     = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm]     = useState(empty);
  const [saving, setSaving] = useState(false);

  const loadList = () =>
    axiosRequest("GET", `${apiBaseUrl}/telegram-cashier`, token).then((res) =>
      setList(Array.isArray(res?.data) ? res.data : [])
    );

  useEffect(() => { loadList(); }, []);

  const openCreate = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit   = (c)  => {
    setEditId(c._id);
    setForm({ name: c.name, phone: c.phone, telegram_chat_id: c.telegram_chat_id || "", telegram_username: c.telegram_username || "", status: !!c.status, role: c.role || "cashier" });
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setEditId(null); setForm(empty); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      getSweetAlert("error", "Name and phone are required");
      return;
    }
    setSaving(true);
    if (editId) {
      await axiosRequest("PUT", `${apiBaseUrl}/telegram-cashier/${editId}`, token, form);
    } else {
      await axiosRequest("POST", `${apiBaseUrl}/telegram-cashier`, token, form);
    }
    setSaving(false);
    getSweetAlert("success", editId ? "Cashier updated" : "Cashier created");
    handleClose();
    loadList();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete cashier?",
      text:  "This cashier will no longer be able to use the bot.",
      icon:  "warning",
      showCancelButton:  true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    await axiosRequest("DELETE", `${apiBaseUrl}/telegram-cashier/${id}`, token);
    getSweetAlert("success", "Cashier deleted");
    loadList();
  };

  const toggleStatus = async (c) => {
    await axiosRequest("PUT", `${apiBaseUrl}/telegram-cashier/${c._id}`, token, { ...c, status: !c.status });
    loadList();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Telegram Cashiers</Typography>
          <Typography color="text.secondary">Manage cashiers authorised to use the Telegram bot</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 2 }}>
          Add Cashier
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ background: "#1c2433" }}>
            <TableRow>
              {["Name", "Phone", "Telegram Chat ID", "Username", "Role", "Status", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  No cashiers added yet
                </TableCell>
              </TableRow>
            ) : (
              list.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell><Typography fontWeight={600}>{c.name}</Typography></TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {c.telegram_chat_id || <span style={{ color: "#aaa" }}>Not linked</span>}
                    </Typography>
                  </TableCell>
                  <TableCell>{c.telegram_username ? `@${c.telegram_username}` : "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={c.role === "super" ? "Super" : "Cashier"}
                      color={c.role === "super" ? "warning" : "default"}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Switch checked={!!c.status} onChange={() => toggleStatus(c)} color="success" size="small" />
                      <Chip
                        label={c.status ? "Active" : "Inactive"}
                        color={c.status ? "success" : "default"}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" size="small" onClick={() => openEdit(c)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" size="small" onClick={() => handleDelete(c._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>{editId ? "Edit Cashier" : "Add Telegram Cashier"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Phone *"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            fullWidth
            inputProps={{ maxLength: 10 }}
          />
          <TextField
            label="Telegram Chat ID"
            value={form.telegram_chat_id}
            onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
            fullWidth
            helperText="Cashier can find their Chat ID via @userinfobot on Telegram"
          />
          <TextField
            label="Telegram Username (optional)"
            value={form.telegram_username}
            onChange={(e) => setForm({ ...form, telegram_username: e.target.value })}
            fullWidth
            InputProps={{ startAdornment: <span style={{ color: "#999", marginRight: 2 }}>@</span> }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={form.role || "cashier"}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <MenuItem value="cashier">Cashier — standard access</MenuItem>
              <MenuItem value="super">Super — includes Loan tab</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.status}
                onChange={(e) => setForm({ ...form, status: e.target.checked })}
                color="success"
              />
            }
            label={form.status ? "Active" : "Inactive"}
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 2 }}>
            {saving ? "Saving…" : editId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
